import { useQuery } from '@tanstack/react-query';

import { useApi } from '../api-context';
import { queryKeys } from '../query-keys';
import {
  appointmentsContextSchema,
  releveDoctolibResultSchema,
  type AppointmentsContext,
  type ReleveDoctolibResult,
} from '../schemas';
import { useToken } from './use-token';

/**
 * Le relevé Doctolib du cabinet : agendas, catégories et motifs.
 *
 * Écrit par l'extension navigateur, jamais par l'app — il n'existe donc aucune
 * mutation ici. Relancer une synchronisation est un geste adressé à
 * l'extension (voir `useSynchroniserDoctolib` côté app), dont le résultat
 * revient par cette requête une fois invalidée.
 *
 * `data.releve` vaut `null` tant que le cabinet n'a jamais synchronisé : c'est
 * un état normal, pas une erreur.
 */
export function useDoctolibReleve() {
  const api = useApi();
  const token = useToken();

  return useQuery({
    queryKey: queryKeys.doctolib.releve(),
    queryFn: () =>
      api.get<ReleveDoctolibResult>('/doctolib', { schema: releveDoctolibResultSchema }),
    enabled: token !== null,
  });
}

/**
 * Où en est la lecture des rendez-vous par l'extension. Relue toutes les cinq
 * minutes : l'écran qui s'en sert avertit quand elle est périmée, et doit se
 * rétablir tout seul une fois l'extension repartie.
 */
export function useAppointmentsContext() {
  const api = useApi();
  const token = useToken();

  return useQuery({
    queryKey: queryKeys.doctolib.appointmentsContext(),
    queryFn: () =>
      api.get<AppointmentsContext>('/doctolib/appointments/context', {
        schema: appointmentsContextSchema,
      }),
    enabled: token !== null,
    refetchInterval: 5 * 60_000,
  });
}
